import { EventDto, MarketDto, SelectionDto } from '../../api/sbapi/sbapi-api-model';
import { BetType } from '../../sportsbook-types';
import { BetBonusType, BetBonus, AcceptMode, NewOffer } from '../../api/betting-service/betting-service-types';

/**
 * Item unrelated from bet
 * Used to extend betItems in order to find additional information of an item
 */
export type BetslipDataItem = {
	ref: string;
	isLoading: boolean;
	eventId: string;
	event?: EventDto;
	marketId: string;
	market?: MarketDto;
	preferredInMarketType?: string;
	selectionId: string;
	selection?: SelectionDto;
	odds: number; // Item odds shared for all bets, is ONLY updated by data
	acceptedOdds: number; // Odds accepted by player, can differ from odds
	hasReplacedData: boolean;
	tag?: string;
};

/**
 * Item in a bet
 * Building blocks of a bet
 */
export type BetslipBetItem = {
	ref: string;
	selectionId: string;
	finalOdds: number; // Item odds specific for the bet, includes everything that can affect odds, can be updated during placement
};

/**
 * Complete BetItem with data
 * Only use finalOdds and finalReturns to display correct values to players
 */
export type BetslipBetItemWithData = BetslipDataItem & BetslipBetItem;

export type BetslipBetItemWithCompleteData = BetslipBetItemWithData & {
	event: EventDto;
	market: MarketDto;
	selection: SelectionDto;
};

export enum BetslipBetErrorType {
	UNAVAILABLE = 'UNAVAILABLE',
	ITEM_UNAVAILABLE = 'ITEM_UNAVAILABLE',
	STAKE_TOO_LOW = 'STAKE_TOO_LOW',
	STAKE_TOO_HIGH = 'STAKE_TOO_HIGH',
	EVENT_LIMIT_EXCEEDED = 'EVENT_LIMIT_EXCEEDED',
	MARKET_LIMIT_EXCEEDED = 'MARKET_LIMIT_EXCEEDED',
	MARKET_LOSS_LIMIT_EXCEEDED = 'MARKET_LOSS_LIMIT_EXCEEDED',
	SELECTION_LIMIT_EXCEEDED = 'SELECTION_LIMIT_EXCEEDED',
	COMBO_NOT_ALLOWED = 'COMBO_NOT_ALLOWED',
	NON_COMBINABLE_SELECTIONS = 'NON_COMBINABLE_SELECTIONS',
	INVALID_BONUS = 'INVALID_BONUS',
	INVALID_BONUS_STAKE_TOO_LOW = 'INVALID_BONUS_STAKE_TOO_LOW',
	INVALID_BONUS_STAKE_TOO_HIGH = 'INVALID_BONUS_STAKE_TOO_HIGH',
	ITEM_COUNT_TOO_LOW = 'ITEM_COUNT_TOO_LOW',
	ITEM_COUNT_TOO_HIGH = 'ITEM_COUNT_TOO_HIGH',
	ODDS_TOO_HIGH = 'ODDS_TOO_HIGH',
	ODDS_TOO_LOW = 'ODDS_TOO_LOW',
	PLAYER_BALANCE_TOO_LOW = 'PLAYER_BALANCE_TOO_LOW',
	PLAYER_STAKE_LIMIT_REACHED = 'PLAYER_STAKE_LIMIT_REACHED',
	PLAYER_LOSS_LIMIT_REACHED = 'PLAYER_LOSS_LIMIT_REACHED',
	ODDS_UPDATED = 'ODDS_UPDATED',
	STAKE_HIGHER_THAN_WIN = 'STAKE_HIGHER_THAN_WIN',
	OTHER = 'OTHER',
	PLAYER_NEEDS_MORE_VERIFICATION_TO_PLACE_BET = 'PLAYER_NEEDS_MORE_VERIFICATION_TO_PLACE_BET',
	PLAYER_AUTH_FAILED = 'PLAYER_AUTH_FAILED',
	PLAYER_BET_LIMIT_PER_DAY_EXCEEDED = 'PLAYER_BET_LIMIT_PER_DAY_EXCEEDED',
	PLAYER_BET_LIMIT_PER_WEEK_EXCEEDED = 'PLAYER_BET_LIMIT_PER_WEEK_EXCEEDED',
	PLAYER_BET_LIMIT_PER_MONTH_EXCEEDED = 'PLAYER_BET_LIMIT_PER_MONTH_EXCEEDED',
	PLAYER_TIME_LIMIT_MISSING = 'PLAYER_TIME_LIMIT_MISSING',
	PLAYER_TIME_LIMIT_PER_DAY_EXCEEDED = 'PLAYER_TIME_LIMIT_PER_DAY_EXCEEDED',
	PLAYER_TIME_LIMIT_PER_WEEK_EXCEEDED = 'PLAYER_TIME_LIMIT_PER_WEEK_EXCEEDED',
	PLAYER_TIME_LIMIT_PER_MONTH_EXCEEDED = 'PLAYER_TIME_LIMIT_PER_MONTH_EXCEEDED',
	PLAYER_IN_COOL_OFF_TIME = 'PLAYER_IN_COOL_OFF_TIME',
	PLAYER_AGE_NOT_VERIFIED = 'PLAYER_AGE_NOT_VERIFIED',
	LUGAS_ACTIVE_ON_OTHER_OPERATOR = 'LUGAS_ACTIVE_ON_OTHER_OPERATOR',
	LUGAS_ON_SITE_COOLOFF = 'LUGAS_ON_SITE_COOLOFF',
	LUGAS_ACTIVE_ON_CURRENT_OPERATOR = 'LUGAS_ACTIVE_ON_CURRENT_OPERATOR',
	PLAYER_STATUS_GAMEPLAY_BLOCKED = 'PLAYER_STATUS_GAMEPLAY_BLOCKED',
	BALANCE_LIMIT_EXCEEDED = 'BALANCE_LIMIT_EXCEEDED',
	SPORTSBOOK_PRODUCT_BLOCKED = 'SPORTSBOOK_PRODUCT_BLOCKED',
	REALITY_CHECK_LIMIT_EXCEEDED = 'REALITY_CHECK_LIMIT_EXCEEDED'
}

/**
 * Generic bet error status in betslip
 */
export type BetslipBetError = {
	betRef: string;
	type: BetslipBetErrorType;
	selectionIds?: Array<string>;
	minStake?: number;
	maxStake?: number;
};

export type BetslipBetWarning = {
	betRef: string;
	type: BetslipBetWarningType;
	selectionIds?: Array<string>;
};

export enum BetslipBetWarningType {
	MAXIMUM_RETURNS_EXCEEDED = 'MAXIMUM_RETURNS_EXCEEDED'
}

/**
 * Placement status of a bet
 */
export enum BetPlacementStatus {
	EDIT = 'EDIT',
	PLACING = 'PLACING',
	PLACED = 'PLACED',
	DECLINED = 'DECLINED',
	UNKNOWN = 'UNKNOWN',
	NEW_OFFER_PENDING = 'NEW_OFFER_PENDING',
	NEW_OFFER_REJECTED = 'NEW_OFFER_REJECTED' // NEW_OFFER_ACCEPTED not needed since we will create a new bet for that
}

type BetslipBetBase<T> = {
	ref: string; // might be changed after creation due to new offer
	originalBetRef: string; // originalBetRef is used to identify the bet in the betslip. should not be changed after creation
	isChecked: boolean; // Is the bet checked or not, false likely means that we should not trust errors array
	placementStatus: BetPlacementStatus;
	type: BetType;
	odds?: number; // odds is the combination of all items odds. Use finalOdds to display to the user!
	originalOdds?: number; // for superBoost
	finalOdds?: number; // finalOdds includes everything. items, taxes, bonuses and other adjustments

	stake: number;
	stakeTax?: number;
	stakeTaxPercentage?: number;

	returns?: number; // Use finalReturns to displaying to the user!
	finalReturns?: number; // finalReturns includes all taxation, bonuses and other adjustments.
	returnsTax?: number;
	returnsTaxPercentage?: number;
	createdDate?: string;
	combination: number;
	betCount: number;
	selectedBonus?: BetBonus;
	minStake?: number;
	maxStake?: number;
	items: Array<T>;
	errors: Array<BetslipBetError>;
	warnings: Array<BetslipBetWarning>;
	bonuses: Array<BetBonus>;
	newOffer?: NewOffer;
	acceptAllOddChanges: boolean;
};

export type BetslipBet = BetslipBetBase<BetslipBetItem>;

/**
 * Complete Bet with data
 */
export type BetslipBetWithData = BetslipBetBase<BetslipBetItemWithData>;

export type BetslipBetWithCompleteData = BetslipBetBase<BetslipBetItemWithCompleteData>;

export type BetslipPersistedItem = {
	ref: string;
	eventId: string;
	marketId: string;
	selectionId: string;
	preferredInMarketType?: string;
	tag?: string;
};

export type BetslipPersistedBet = {
	type: BetType;
	stake: number;
	items: Array<BetslipPersistedItem>;
	combination: number;
};

export enum BetslipLimitation {
	BASIC_ERROR = 'BASIC_ERROR',
	UNAUTHORIZED_USER_ERROR = 'UNAUTHORIZED_USER_ERROR',
	BALANCE_TOO_LOW_ERROR = 'BALANCE_TOO_LOW_ERROR',
	PRODUCT_BALANCE_TOO_LOW_ERROR = 'PRODUCT_BALANCE_TOO_LOW_ERROR',
	BETSLIP_CHANGES_NOT_ACCEPTED = 'BETSLIP_CHANGES_NOT_ACCEPTED',
	NO_STAKE_ERROR = 'NO_STAKE_ERROR',
	NOT_COMBINABLE_GROUP = 'NOT_COMBINABLE_GROUP',
	NEW_OFFER = 'NEW_OFFER',
	COMBO_ODDS_TOO_HIGH = 'COMBO_ODDS_TOO_HIGH',
	IS_LOADING = 'LOADING',
	BET_HAS_AN_ERROR = 'BET_HAS_AN_ERROR',
	LOCATION_NOT_SHARED = 'LOCATION_NOT_SHARED',
	LOCATION_PENDING = 'LOCATION_PENDING',
	LOCATION_DENIED = 'LOCATION_DENIED',
	LOCATION_ERROR = 'LOCATION_ERROR'
}

export type BetslipPendingItem = {
	selection: SelectionDto;
	tag: string | undefined;
};

export enum BetslipTab {
	SINGLES = 'SINGLES',
	COMBO = 'COMBO',
	SYSTEM = 'SYSTEM'
}
export enum BetslipPlacementStatus {
	EDIT = 'EDIT',
	PLACEMENT = 'PLACEMENT',
	RECEIPT = 'RECEIPT'
}

export { AcceptMode };
export { BetBonusType };
export type { BetBonus };
